import React, { useMemo } from "react";
import { RiPhoneLine, RiMapPinLine } from "@remixicon/react";
import { Typography } from "@/components/ui/typography";
import DashboardCardWrapper from "../common/DashboardCardWrapper";
import { CardContent } from "../ui/card";
import DashboardCardHeader from "../common/DashboardCardHeader";
import PCPsIcon from "@/assets/icons/pcp.svg";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { CardSkeleton } from "@/components/ui/skeleton";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import {
  getInitialsFromName,
  formatPhoneNumber,
} from "@/constants/utilityFunctions";
import { routes } from "@/utils/urls";
import { TruncatedTextTooltip } from "../common/TextTooltip";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";
import DashboardFieldIcon from "./common/DashboardFieldIcon";

interface PcpData {
  memberPcpName: string;
  memberPcpPhone: string;
  memberPcpAddress1: string;
  memberPcpAddress2: string;
  memberPcpCity: string;
  memberPcpState: string;
}

// Handle null values - replace with "-"
const handleNullValues = (data: PcpData | null): PcpData | null => {
  if (!data || typeof data !== "object") return data;

  const processed: any = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === "") {
      processed[key] = "-";
    } else {
      processed[key] = value;
    }
  }
  return processed as PcpData;
};

const fieldIconMap: Record<
  string,
  React.ComponentType<{ size?: string | number; className?: string }> | string
> = {
  "Phone Number": RiPhoneLine,
  Address: RiMapPinLine,
  City: "ri-community-line",
  State: "ri-government-line",
};

interface PCPCardProps {
  shouldBlink?: boolean;
}

const PCPCard: React.FC<PCPCardProps> = ({ shouldBlink = false }) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("PCP");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: pcp,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<PcpData>({
    endpoint: routes.cards.pcp,
    memberId,
    processData: (rawData: unknown) =>
      handleNullValues((rawData as { data: PcpData }).data),
  });

  const display = useMemo(() => {
    if (!pcp) return null;

    // Concatenate address1 and address2, filter out "-" values
    const addressParts = [pcp.memberPcpAddress1, pcp.memberPcpAddress2].filter(
      (part) => part && part !== "-"
    );
    const fullAddress = addressParts.length > 0 ? addressParts.join(", ") : "-";

    return {
      pcpName: pcp.memberPcpName || "-",
      fields: {
        "Phone Number": formatPhoneNumber(pcp.memberPcpPhone),
        Address: fullAddress,
        City: pcp.memberPcpCity,
        State: pcp.memberPcpState,
      } as Record<string, string>,
    };
  }, [pcp]);

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <img
                src={PCPsIcon}
                alt='PCP'
                className='w-4 h-4 fill-icon-pcp-500 dark:fill-icon-pcp-400'
              />
            </BorderedIconWrapper>
          }
          title='Primary Care Provider (PCP)'
          className='h-[56px]'
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />

        <CardContent className='p-4 lg:h-[257px] md:h-[230px] sm:h-full lg:mb-[28px] md:mb-[28px]'>
          {(() => {
            if (loading) {
              return <CardSkeleton rows={6} showProfile={true} />;
            }

            if (error) {
              return (
                <div className='flex items-center justify-center h-full lg:h-[230px] md:h-[230px]'>
                  <Typography
                    variant='medium-14'
                    className={`${isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                      } font-medium text-center`}>
                    {error}
                  </Typography>
                </div>
              );
            }

            if (!pcp || !display) {
              return null;
            }

            return (
              <>
              <div className='flex items-center gap-3 mb-4'>
                <div className='w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center border-[1px] border-grey-300 dark:border-[1px] dark:border-grey-300'>
                  <Typography variant='semibold-16' className='text-blue-600'>
                    {display.pcpName && display.pcpName !== "-"
                      ? getInitialsFromName(display.pcpName)
                      : "-"}
                  </Typography>
                </div>
                <TruncatedTextTooltip text={display.pcpName || "-"}>
                  <Typography
                    variant='medium-16'
                    className='text-grey-800 dark:text-white truncate'>
                    {display.pcpName || "-"}
                  </Typography>
                </TruncatedTextTooltip>
              </div>
              <div className='gap-[16px] flex flex-col'>
                {Object.entries(display.fields).map(([key, value]) => {
                  const IconComponent = fieldIconMap[key];

                  return (
                    <div key={key} className='flex items-start gap-3'>
                      <DashboardFieldIcon icon={IconComponent} />

                      <div className='flex-1 flex justify-between items-start min-w-0'>
                        <Typography
                          variant='medium-14'
                          className='text-grey-700 dark:text-gray-400 flex-shrink-0 leading-[1.6]'>
                          {key}
                        </Typography>

                        <div className='flex items-center gap-2 min-w-0 max-w-[60%]'>
                          <TruncatedTextTooltip
                            text={value}
                            className='min-w-0 flex-1'>
                            <Typography
                              variant='medium-14'
                              className='
                    text-gray-800 dark:text-white text-right truncate
                    max-md:whitespace-normal max-md:break-words max-md:text-right max-md:block
                    leading-[1.6]
                  '>
                              {value}
                            </Typography>
                          </TruncatedTextTooltip>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            );
          })()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default PCPCard;
